import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormPasswordInput, SubmitButton } from '@/components/form'
import { usersService } from '@/services/users'
import { handleApiError } from '@/utils/errors'

interface EditPasswordModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function EditPasswordModal({ open, onOpenChange, onSuccess }: EditPasswordModalProps) {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!currentPassword.trim()) {
      setError(t('profile.editPassword.currentPasswordRequired'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('profile.editPassword.mismatch'))
      return
    }
    if (password.length < 8) {
      setError(t('profile.editPassword.tooShort'))
      return
    }
    setIsSubmitting(true)
    try {
      await usersService.changePassword({
        current_password: currentPassword,
        new_password: password,
      })
      onSuccess()
      onOpenChange(false)
      setCurrentPassword('')
      setPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(handleApiError(err).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="bg-card border-border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border p-6 shadow-lg">
          <Dialog.Title className="text-foreground text-lg font-semibold">
            {t('profile.editPassword.title')}
          </Dialog.Title>
          <Dialog.Description className="text-muted-foreground mt-1 text-sm">
            {t('profile.editPassword.description')}
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
            {error && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}
            <FormPasswordInput
              id="edit-current-password"
              label={t('profile.editPassword.currentPassword')}
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isSubmitting}
              showPasswordLabel={t('login.showPassword')}
              hidePasswordLabel={t('login.hidePassword')}
            />
            <FormPasswordInput
              id="edit-password"
              label={t('profile.editPassword.newPassword')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              showPasswordLabel={t('login.showPassword')}
              hidePasswordLabel={t('login.hidePassword')}
            />
            <FormPasswordInput
              id="edit-password-confirm"
              label={t('profile.editPassword.confirmPassword')}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              showPasswordLabel={t('login.showPassword')}
              hidePasswordLabel={t('login.hidePassword')}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="border-border text-foreground hover:bg-muted rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                >
                  {t('profile.editProfile.cancel')}
                </button>
              </Dialog.Close>
              <SubmitButton
                isLoading={isSubmitting}
                loadingLabel={t('profile.editPassword.submitting')}
                showIcon={false}
              >
                {t('profile.editPassword.submit')}
              </SubmitButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
